import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, useLang, useUnits, formatCurrency, formatApiError, usePerm } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, TextArea, Select, EmptyState } from "../components/primitives";
import BulkActionBar, { Checkbox } from "../components/BulkActionBar";
import { useConfirm } from "../components/ConfirmDialog";
import PresenceBar from "../components/PresenceBar";
import usePresence from "../hooks/usePresence";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, X, CaretDown, CaretRight,
  FunnelSimple, SortAscending, DownloadSimple, UploadSimple, FileText,
} from "@phosphor-icons/react";

const emptyPos = { name: "", description: "", category_id: "", unit: "Stk", unit_price: 0, tiers: [] };

export default function PositionsPage() {
  const { t, lang } = useLang();
  const { units } = useUnits();
  const confirm = useConfirm();
  const { can, hidesPrices, isAdmin, isExpert } = usePerm();
  const canCreate = can("positions", "create");
  const canEdit = can("positions", "edit");
  const canDelete = can("positions", "delete");
  const noPrices = hidesPrices("positions");
  const showPurchase = isAdmin || isExpert;  // EK only for admin/expert

  // Page-level presence
  const { others } = usePresence({ resource: "positions", enabled: true });
  const [positions, setPositions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("details");

  // Edit-lock for currently opened item (only existing ones)
  const editingId = editing?.id || null;
  const { holder: editHolder } = usePresence({ resource: "positions", itemId: editingId, edit: !!editingId, enabled: !!editingId });

  // Filters
  const [q, setQ] = useState("");
  const [filterCats, setFilterCats] = useState([]); // array of ids
  const [filterUnit, setFilterUnit] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sort, setSort] = useState(() => localStorage.getItem("positions_sort") || "number");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => { localStorage.setItem("positions_sort", sort); }, [sort]);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [collapsedCats, setCollapsedCats] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const p = await api.get("/positions");
      setPositions(p.data);
      try {
        const c = await api.get("/categories");
        setCategories(c.data);
      } catch {
        // Users without categories permission still need to see positions grouped by id
        setCategories([]);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Body scroll lock while modal is open — prevents background scrolling
  // and guarantees the modal stays anchored to the viewport top.
  useEffect(() => {
    if (!editing) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [editing]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);

  const filtered = useMemo(() => {
    let list = [...positions];
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(qq) ||
        (p.description || "").toLowerCase().includes(qq) ||
        (p.number || "").toLowerCase().includes(qq)
      );
    }
    if (filterCats.length) list = list.filter((p) => filterCats.includes(p.category_id || "__none"));
    if (filterUnit) list = list.filter((p) => p.unit === filterUnit);
    if (priceMin !== "") list = list.filter((p) => Number(p.unit_price) >= Number(priceMin));
    if (priceMax !== "") list = list.filter((p) => Number(p.unit_price) <= Number(priceMax));

    list.sort((a, b) => {
      if (sort === "name") return (a.name || "").localeCompare(b.name || "");
      if (sort === "price_asc") return Number(a.unit_price || 0) - Number(b.unit_price || 0);
      if (sort === "price_desc") return Number(b.unit_price || 0) - Number(a.unit_price || 0);
      return (a.number || "").localeCompare(b.number || ""); // default: number
    });
    return list;
  }, [positions, q, filterCats, filterUnit, priceMin, priceMax, sort]);

  const grouped = useMemo(() => {
    const g = new Map();
    filtered.forEach((p) => {
      const key = p.category_id || "__none";
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(p);
    });
    // order groups by category number
    return Array.from(g.entries()).sort(([a], [b]) => {
      const ca = catMap[a]?.number ?? 99;
      const cb = catMap[b]?.number ?? 99;
      return ca - cb;
    });
  }, [filtered, catMap]);

  const toggleCat = (id) => setCollapsedCats((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const toggleFilterCat = (id) => {
    setFilterCats((cats) => cats.includes(id) ? cats.filter((c) => c !== id) : [...cats, id]);
  };

  const resetFilters = () => {
    setQ(""); setFilterCats([]); setFilterUnit(""); setPriceMin(""); setPriceMax(""); setSort("number");
  };

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAll = (val) => setSelected(val ? new Set(filtered.map((p) => p.id)) : new Set());

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: editing.name,
      number: editing.number || null,
      description: editing.description || "",
      category_id: editing.category_id || null,
      unit: editing.unit || "Stk",
      unit_price: Number(editing.unit_price) || 0,
      purchase_price: editing.purchase_price === "" || editing.purchase_price === null || editing.purchase_price === undefined
        ? null
        : Number(editing.purchase_price),
      tiers: (editing.tiers || [])
        .filter((tr) => tr.min_quantity !== "" && tr.unit_price !== "")
        .map((tr) => ({ min_quantity: Number(tr.min_quantity), unit_price: Number(tr.unit_price) })),
    };
    if (editing.id) await api.put(`/positions/${editing.id}`, payload);
    else await api.post("/positions", payload);
    setEditing(null);
    load();
  };

  const delOne = async (p) => {
    const ok = await confirm({
      title: `${t("confirmDeleteTitle")} ${p.number} ${p.name}`,
      description: t("confirmHardDeleteDesc"),
      confirmText: t("deleteButton"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/positions/${p.id}`);
    load();
  };

  const bulkDel = async () => {
    const ids = [...selected];
    const ok = await confirm({
      title: t("confirmDeleteTitle"),
      description: t("confirmDeleteMany").replace("{n}", ids.length),
      confirmText: t("deleteButton"),
      variant: "danger",
    });
    if (!ok) return;
    await api.post("/positions/bulk-delete", { ids });
    setSelected(new Set());
    load();
  };

  const bulkCopy = async () => {
    const ids = [...selected];
    await api.post("/positions/bulk-duplicate", { ids });
    setSelected(new Set());
    load();
  };

  const addTier = () => setEditing({ ...editing, tiers: [...(editing.tiers || []), { min_quantity: "", unit_price: "" }] });
  const updateTier = (i, k, v) => { const tiers = [...editing.tiers]; tiers[i] = { ...tiers[i], [k]: v }; setEditing({ ...editing, tiers }); };
  const removeTier = (i) => setEditing({ ...editing, tiers: editing.tiers.filter((_, idx) => idx !== i) });

  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));

  return (
    <div className="p-6 sm:p-8 max-w-[1400px] mx-auto">
      <div className="sticky top-0 z-20 -mx-6 sm:-mx-8 px-6 sm:px-8 pt-2 pb-3 bg-[var(--bg)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg)]/80 border-b border-[var(--border)]/60">
      <PageHeader
        eyebrow="04 / Katalog"
        title={t("positions")}
        description={lang === "de"
          ? "Positionen gruppiert nach Oberkategorien — mit fortlaufenden Nummern (z. B. 01.100)."
          : "Positions grouped by category — with sequential numbers (e.g. 01.100)."}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <PresenceBar others={others} lang={lang} />
            <CsvActions lang={lang} canCreate={canCreate} onImported={load} />
            {canCreate ? (
              <Btn data-testid="new-position-btn" onClick={() => { setEditing({ ...emptyPos }); setTab("details"); }}>
                <Plus size={16} /> {t("newPosition")}
              </Btn>
            ) : null}
          </div>
        }
      />

      {editing && createPortal(
        <div
          className="fixed inset-0 z-[100] overflow-y-auto bg-black/40 backdrop-blur-sm"
          data-testid="position-modal-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditing(null); }}
        >
          <div className="min-h-full flex items-start justify-center p-4 sm:p-8">
          <form
            onSubmit={save}
            className="swiss-card w-full max-w-3xl my-4"
            data-testid="position-form"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4 sticky top-0 bg-[var(--surface)] z-10">
              <h2 className="text-base font-medium text-[var(--ink)]">
                {editing.id ? t("editPosition") : t("newPosition")}
              </h2>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-[var(--muted)] hover:text-[var(--ink)] -mr-2 p-2"
                data-testid="position-modal-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          {editHolder && (
            <div className="bg-[#F59E0B]/10 border-b border-[#F59E0B]/30 px-5 py-3 text-sm text-[#92400E] flex items-center gap-2" data-testid="position-lock-warning">
              <span className="inline-block w-2 h-2 rounded-full bg-[#F59E0B]" />
              {lang === "de"
                ? <>Achtung: <b>{editHolder.user_name || editHolder.user_email}</b> bearbeitet diese Position gerade.</>
                : <>Warning: <b>{editHolder.user_name || editHolder.user_email}</b> is currently editing this position.</>}
            </div>
          )}
          <div className="border-b border-[var(--border)] flex">
            <button type="button" onClick={() => setTab("details")}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${tab === "details" ? "border-[var(--brand)] text-[var(--brand)]" : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"}`}
              data-testid="tab-details">{t("tabDetails")}</button>
            <button type="button" onClick={() => setTab("tiers")}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${tab === "tiers" ? "border-[var(--brand)] text-[var(--brand)]" : "border-transparent text-[var(--muted)] hover:text-[var(--ink)]"}`}
              data-testid="tab-tiers">{t("tabTiers")}</button>
          </div>
          <div className="p-6">
            {tab === "details" ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {editing.id && (
                  <Field label={t("positionNumber")} hint={lang === "de" ? "Automatisch vergeben, nicht editierbar." : "Auto-assigned, not editable."}>
                    <TextInput data-testid="position-number" value={editing.number || ""} readOnly disabled />
                  </Field>
                )}
                <Field label={t("positionName")} className={editing.id ? "md:col-span-2" : "md:col-span-3"}>
                  <TextInput required data-testid="position-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </Field>
                <Field label={t("category")}>
                  <Select data-testid="position-category" value={editing.category_id || ""} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}>
                    <option value="">{t("uncategorized")}</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{String(c.number || "?").padStart(2, "0")} · {c.name}</option>)}
                  </Select>
                </Field>
                <Field label={t("unit")}>
                  <Select data-testid="position-unit" value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })}>
                    {units.map((u) => <option key={u.value} value={u.value}>{lang === "de" ? u.label_de : u.label_en} ({u.value})</option>)}
                  </Select>
                </Field>
                <Field label={(lang === "de" ? "Verkaufspreis" : "Sales price") + " (€)"}>
                  <TextInput type="number" step="0.01" data-testid="position-unit-price" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })} />
                </Field>
                {showPurchase && (
                  <Field
                    label={(lang === "de" ? "Einkaufspreis" : "Purchase price") + " (€)"}
                    hint={(() => {
                      const ek = Number(editing.purchase_price) || 0;
                      const vk = Number(editing.unit_price) || 0;
                      if (!ek || !vk) return lang === "de" ? "Nur intern — wird nicht in Angeboten/Rechnungen angezeigt." : "Internal only — never shown in quotes/invoices.";
                      const margin = vk - ek;
                      const pct = vk > 0 ? (margin / vk) * 100 : 0;
                      return `${lang === "de" ? "Marge" : "Margin"}: ${margin.toLocaleString(lang === "de" ? "de-DE" : "en-US", { style: "currency", currency: "EUR" })} (${pct.toFixed(1)} %)`;
                    })()}
                  >
                    <TextInput
                      type="number"
                      step="0.01"
                      data-testid="position-purchase-price"
                      value={editing.purchase_price ?? ""}
                      onChange={(e) => setEditing({ ...editing, purchase_price: e.target.value })}
                      placeholder={lang === "de" ? "z.B. 129,50" : "e.g. 129.50"}
                    />
                  </Field>
                )}
                <Field label={t("description")} className="md:col-span-3">
                  <TextArea data-testid="position-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                </Field>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-[var(--muted-2)]">
                    {lang === "de" ? "Reduzierter Preis ab einer bestimmten Menge." : "Reduced price from a given quantity."}
                  </p>
                  <Btn type="button" variant="secondary" onClick={addTier} data-testid="add-tier-btn" className="h-8"><Plus size={14} /> {t("addTier")}</Btn>
                </div>
                {(editing.tiers || []).length === 0 ? (
                  <div className="text-sm text-[var(--muted-2)] py-6 text-center">
                    {lang === "de" ? "Noch keine Staffel hinterlegt." : "No tiers yet."}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {editing.tiers.map((tr, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-[var(--muted-2)]">{t("minQty")}</label>
                            <TextInput type="number" step="0.01" value={tr.min_quantity} onChange={(e) => updateTier(i, "min_quantity", e.target.value)} data-testid={`tier-qty-${i}`} />
                          </div>
                          <div>
                            <label className="text-xs text-[var(--muted-2)]">{t("unitPrice")} (€)</label>
                            <TextInput type="number" step="0.01" value={tr.unit_price} onChange={(e) => updateTier(i, "unit_price", e.target.value)} data-testid={`tier-price-${i}`} />
                          </div>
                        </div>
                        <button type="button" onClick={() => removeTier(i)} data-testid={`tier-remove-${i}`} className="mt-5 text-[var(--muted)] hover:text-[#FF3B30]">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 mt-6">
              <Btn type="submit" data-testid="position-save">{t("save")}</Btn>
              <Btn type="button" variant="ghost" onClick={() => setEditing(null)} data-testid="position-cancel">{t("cancel")}</Btn>
            </div>
          </div>
        </form>
        </div>
        </div>,
        document.body
      )}

      {/* Filter bar */}
      <div className="swiss-card p-4 mb-4 mt-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-2)]" />
            <TextInput placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="positions-search" />
          </div>
          <Select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="positions-sort" className="w-auto min-w-[160px]">
            <option value="number">{t("sortBy")}: {t("sortNumber")}</option>
            <option value="name">{t("sortBy")}: {t("sortName")}</option>
            <option value="price_asc">{t("sortBy")}: {t("sortPriceAsc")}</option>
            <option value="price_desc">{t("sortBy")}: {t("sortPriceDesc")}</option>
          </Select>
          <Btn variant="secondary" onClick={() => setFiltersOpen(!filtersOpen)} data-testid="toggle-filters" className="h-10">
            <FunnelSimple size={14} /> {t("filters")}
            {(filterCats.length + (filterUnit ? 1 : 0) + (priceMin !== "" ? 1 : 0) + (priceMax !== "" ? 1 : 0) > 0) && (
              <span className="ml-1 px-1.5 text-xs bg-[var(--brand)] text-white rounded-sm">
                {filterCats.length + (filterUnit ? 1 : 0) + (priceMin !== "" ? 1 : 0) + (priceMax !== "" ? 1 : 0)}
              </span>
            )}
          </Btn>
        </div>

        {filtersOpen && (
          <div className="mt-4 pt-4 border-t border-[var(--border)] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="label-overline mb-2">{t("category")}</div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {categories.map((c) => {
                  const active = filterCats.includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => toggleFilterCat(c.id)}
                      data-testid={`filter-cat-${c.id}`}
                      className={`px-2 py-1 text-xs rounded-sm border transition-colors ${active ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--muted)] hover:bg-[var(--row-hover)]"}`}>
                      {String(c.number || "?").padStart(2, "0")} {c.name}
                    </button>
                  );
                })}
                <button type="button" onClick={() => toggleFilterCat("__none")}
                  className={`px-2 py-1 text-xs rounded-sm border transition-colors ${filterCats.includes("__none") ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--muted)] hover:bg-[var(--row-hover)]"}`}>
                  {t("uncategorized")}
                </button>
              </div>
            </div>
            <div>
              <div className="label-overline mb-2">{t("unit")}</div>
              <Select value={filterUnit} onChange={(e) => setFilterUnit(e.target.value)} data-testid="filter-unit">
                <option value="">{t("allUnits")}</option>
                {units.map((u) => <option key={u.value} value={u.value}>{u.value}</option>)}
              </Select>
            </div>
            <div>
              <div className="label-overline mb-2">{t("priceMin")}</div>
              <TextInput type="number" step="0.01" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} data-testid="filter-price-min" />
            </div>
            <div>
              <div className="label-overline mb-2">{t("priceMax")}</div>
              <TextInput type="number" step="0.01" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} data-testid="filter-price-max" />
            </div>
            <div className="md:col-span-2 lg:col-span-4">
              <Btn variant="ghost" onClick={resetFilters} data-testid="reset-filters" className="h-8">
                <X size={14} /> {t("resetFilters")}
              </Btn>
            </div>
          </div>
        )}
      </div>
      </div>{/* /sticky-toolbar */}

      <BulkActionBar
        count={selected.size}
        lang={lang}
        onClear={() => setSelected(new Set())}
        onCopy={bulkCopy}
        onDelete={bulkDel}
      />

      {loading ? (
        <div className="swiss-card p-8 text-[var(--muted)]">…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title={t("noPositions")} action={canCreate ? <Btn onClick={() => { setEditing({ ...emptyPos }); setTab("details"); }} data-testid="empty-new-position"><Plus size={16} /> {t("newPosition")}</Btn> : null} />
      ) : (
        <div className="space-y-4">
          {/* Select-all row */}
          {(canEdit || canDelete) && (
            <div className="flex items-center gap-3 px-4 py-2 text-xs text-[var(--muted)]">
              <Checkbox checked={allSelected} onChange={selectAll} testId="positions-select-all" />
              <span>{t("selectAll")} ({filtered.length})</span>
            </div>
          )}

          {grouped.map(([catKey, items]) => {
            const cat = catMap[catKey];
            const collapsed = collapsedCats.has(catKey);
            return (
              <div key={catKey} className="swiss-card overflow-hidden" data-testid={`cat-group-${catKey}`}>
                <button onClick={() => toggleCat(catKey)}
                  className="w-full flex items-center gap-3 px-5 py-3 bg-[var(--surface-alt)] hover:bg-[var(--row-hover)] transition-colors border-b border-[var(--border)]">
                  {collapsed ? <CaretRight size={16} /> : <CaretDown size={16} />}
                  <span className="font-mono text-xs px-2 py-0.5 bg-[var(--brand-soft)] text-[var(--brand)] rounded-sm">
                    {String(cat?.number || "??").padStart(2, "0")}
                  </span>
                  <span className="font-heading font-medium text-[var(--ink)]">{cat?.name || t("uncategorized")}</span>
                  <span className="text-xs text-[var(--muted-2)] ml-2">{items.length}</span>
                </button>
                {!collapsed && (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-[var(--bg)] text-[var(--muted)]">
                      <tr>
                        {(canEdit || canDelete) && <th className="w-8 pl-5"></th>}
                        <th className="text-left font-medium px-3 py-2 label-overline w-24">{t("positionNumber")}</th>
                        <th className="text-left font-medium px-3 py-2 label-overline">{t("positionName")}</th>
                        <th className="text-left font-medium px-3 py-2 label-overline w-20">{t("unit")}</th>
                        {!noPrices && <th className="text-right font-medium px-3 py-2 label-overline w-28">{t("unitPrice")}</th>}
                        {!noPrices && <th className="text-center font-medium px-3 py-2 label-overline w-24">{t("tabTiers")}</th>}
                        {(canEdit || canDelete) && <th className="text-right font-medium px-5 py-2 label-overline w-28">{t("actions")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((p) => (
                        <tr key={p.id} className={`border-t border-[var(--border)] transition-colors fade-up ${selected.has(p.id) ? "bg-[var(--brand-soft)]" : "hover:bg-[var(--row-hover)]"}`}>
                          {(canEdit || canDelete) && (
                            <td className="pl-5">
                              <Checkbox checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} testId={`sel-pos-${p.id}`} />
                            </td>
                          )}
                          <td className="px-3 py-3 font-mono text-[var(--muted)]">{p.number || "—"}</td>
                          <td className="px-3 py-3">
                            <div className="font-medium">{p.name}</div>
                            {p.description && <div className="text-xs text-[var(--muted-2)] mt-0.5 line-clamp-1">{p.description}</div>}
                          </td>
                          <td className="px-3 py-3 font-mono text-[var(--muted)]">{p.unit}</td>
                          {!noPrices && <td className="px-3 py-3 text-right font-medium">{formatCurrency(p.unit_price, lang)}</td>}
                          {!noPrices && <td className="px-3 py-3 text-center text-[var(--muted)]">{p.tiers?.length || 0}</td>}
                          {(canEdit || canDelete) && (
                            <td className="px-5 py-3 text-right">
                              <div className="inline-flex gap-1">
                                {canEdit && <Btn variant="ghost" onClick={() => { setEditing(p); setTab("details"); }} data-testid={`edit-position-${p.id}`} className="h-8 px-2"><PencilSimple size={14} /></Btn>}
                                {canDelete && <Btn variant="ghost" onClick={() => delOne(p)} data-testid={`delete-position-${p.id}`} className="h-8 px-2 text-[#FF3B30] hover:bg-[#FF3B30]/5"><Trash size={14} /></Btn>}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CsvActions({ lang, canCreate, onImported }) {
  const [busy, setBusy] = React.useState(false);
  const [importResult, setImportResult] = React.useState(null);
  const inputRef = React.useRef(null);

  const downloadBlob = async (path, filename) => {
    setBusy(true);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/positions/import.csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      onImported && onImported();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <Btn variant="ghost" disabled={busy} onClick={() => downloadBlob("/positions/export.csv", "positionen.csv")} data-testid="positions-export" className="h-9 px-3">
        <DownloadSimple size={14} /> {lang === "de" ? "Export" : "Export"}
      </Btn>
      <Btn variant="ghost" disabled={busy} onClick={() => downloadBlob("/positions/template.csv", "positionen-vorlage.csv")} data-testid="positions-template" className="h-9 px-3">
        <FileText size={14} /> {lang === "de" ? "Vorlage" : "Template"}
      </Btn>
      {canCreate && (
        <>
          <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="positions-import-input" />
          <Btn variant="ghost" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="positions-import" className="h-9 px-3">
            <UploadSimple size={14} /> {lang === "de" ? "Import" : "Import"}
          </Btn>
        </>
      )}

      {importResult && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setImportResult(null)}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-sm w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="positions-import-result">
            <div className="px-6 py-4 border-b border-[var(--border)]">
              <div className="label-overline">{lang === "de" ? "CSV-Import" : "CSV import"}</div>
              <h3 className="font-heading text-lg font-semibold">{lang === "de" ? "Import abgeschlossen" : "Import done"}</h3>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 border border-[var(--border)] rounded-sm">
                  <div className="text-2xl font-semibold text-[var(--brand)]" data-testid="import-created-count">{importResult.created || 0}</div>
                  <div className="text-xs text-[var(--muted-2)]">{lang === "de" ? "neu erstellt" : "created"}</div>
                </div>
                <div className="text-center p-3 border border-[var(--border)] rounded-sm">
                  <div className="text-2xl font-semibold" data-testid="import-updated-count">{importResult.updated || 0}</div>
                  <div className="text-xs text-[var(--muted-2)]">{lang === "de" ? "aktualisiert" : "updated"}</div>
                </div>
                <div className="text-center p-3 border border-[var(--border)] rounded-sm">
                  <div className="text-2xl font-semibold text-[#F59E0B]" data-testid="import-skipped-count">{importResult.skipped || 0}</div>
                  <div className="text-xs text-[var(--muted-2)]">{lang === "de" ? "übersprungen" : "skipped"}</div>
                </div>
              </div>
              {importResult.items_created?.length > 0 && (
                <details className="border border-[var(--border)] rounded-sm" data-testid="import-section-created">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium hover:bg-[var(--row-hover)]">
                    {lang === "de" ? `Neu erstellt (${importResult.items_created.length})` : `Created (${importResult.items_created.length})`}
                  </summary>
                  <ul className="px-3 py-2 max-h-40 overflow-y-auto text-xs space-y-0.5">
                    {importResult.items_created.map((it, i) => (
                      <li key={i} className="text-[var(--muted)]">• {it.number} — {it.name}</li>
                    ))}
                  </ul>
                </details>
              )}
              {importResult.items_updated?.length > 0 && (
                <details className="border border-[var(--border)] rounded-sm" data-testid="import-section-updated">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium hover:bg-[var(--row-hover)]">
                    {lang === "de" ? `Aktualisiert (${importResult.items_updated.length})` : `Updated (${importResult.items_updated.length})`}
                  </summary>
                  <ul className="px-3 py-2 max-h-40 overflow-y-auto text-xs space-y-0.5">
                    {importResult.items_updated.map((it, i) => (
                      <li key={i} className="text-[var(--muted)]">• {it.number} — {it.name}</li>
                    ))}
                  </ul>
                </details>
              )}
              {importResult.items_skipped?.length > 0 && (
                <details className="border border-[#F59E0B]/40 rounded-sm bg-[#F59E0B]/5" open data-testid="import-section-skipped">
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium hover:bg-[#F59E0B]/10">
                    {lang === "de" ? `Übersprungen — bereits vorhanden (${importResult.items_skipped.length})` : `Skipped — already existing (${importResult.items_skipped.length})`}
                  </summary>
                  <ul className="px-3 py-2 max-h-40 overflow-y-auto text-xs space-y-0.5">
                    {importResult.items_skipped.map((it, i) => (
                      <li key={i} className="text-[var(--muted)]">• {it.number || "—"} — {it.name}</li>
                    ))}
                  </ul>
                </details>
              )}
              {importResult.errors?.length > 0 && (
                <div className="text-xs text-[#F59E0B]">
                  <div className="font-medium mb-1">{lang === "de" ? "Hinweise:" : "Notes:"}</div>
                  <ul className="space-y-0.5">
                    {importResult.errors.slice(0, 10).map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end">
              <Btn onClick={() => setImportResult(null)} data-testid="positions-import-result-close">{lang === "de" ? "Schließen" : "Close"}</Btn>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
